import {StoreServiceGenInfoFormFieldsType} from "../../../../../../components/offering-form/services/gen-info/types";
import {StoreServiceSettingsFormFieldsType} from "../../../../../../components/offering-form/services/settings/types";
import {StoreServicePricingFormFieldsType} from "../../../../../../components/offering-form/services/pricing/types";
import {StoreServiceDeliveryTimeFrameFormFields} from "../../../../../../components/offering-form/services/delivery-timeframe/types";
import yup from "../../../../../../utils/yup-extended";
import {pwywCurrencyRowSchema} from "@/features/pricing";
import {IS_PWYW_ENABLED} from "../../../../../../services/config";
import {purchaseLimitValidationSchemaFields} from "../../../../../../components/offering-form/general/settings/validation-schema";

/**
 * A currency row under Free or Single Fee — unchanged from before Pay What You Want
 * existed, deliberately: shipping the third model must not move the other two.
 */
const singleFeeCurrencyRowSchema = yup.object().shape({
  callPriceCurrency: yup.string().trim().required("Currency is required"),
  callPrice: yup
    .number()
    .required("Price is required")
    .min(1, "Can't be less than 1")
    .default(1)
    .typeError("Must be a number"),
});

/** With the flag off, Pay What You Want is not a Pricing Model a coach can submit. */
const selectablePriceTypes = IS_PWYW_ENABLED
  ? ["paid", "free", "pwyw"]
  : ["paid", "free"];

export const createStoreServiceBasicFormStepFormValidationSchema = yup.object().shape({
  name: yup.string().trim().required("Service name is required"),
  description: yup.string().trim().required("Service description is required"),
  priceType: yup.string().required().oneOf(selectablePriceTypes, "Select a cost"),
  currencies: yup.array().when("priceType", (priceType: string, schema) => {
    if (IS_PWYW_ENABLED && priceType === "pwyw") {
      return schema.of(pwywCurrencyRowSchema);
    }

    // Free keeps one blank row so the coach has a way back to a priced model (see
    // `transitionOfferingFormCurrencyRows`). That row is never rendered under Free, so
    // holding it to a Single Fee price would block the save with an error the coach can
    // neither see nor clear.
    if (priceType === "free") {
      return schema;
    }

    return schema.of(singleFeeCurrencyRowSchema);
  }),
  deliveryTimeFrameUnit: yup
    .string()
    .required()
    .oneOf(["days", "weeks", "months"], "Select a delivery time frame unit"),
  deliveryTimeFrameValue: yup
    .number()
    .required("Delivery time frame value is required")
    .min(1, "Can't be less than 1")
    .default(1)
    .typeError("Must be a number"),
  hideServiceFromMenu: yup.bool().default(false),
  requestCustomerPhoneNumber: yup.bool().default(false),
  ...purchaseLimitValidationSchemaFields,
  isUrlRedirect: yup.bool().default(false),
  redirectAfterPurchase: yup
    .mixed<"none" | "telegram" | "custom-url">()
    .oneOf(["none", "telegram", "custom-url"])
    .default("none"),
  telegramGroupId: yup.string().when("redirectAfterPurchase", {
    is: "telegram",
    then: (schema) =>
      schema.required("Telegram group is required").test({
        name: "telegram-group",
        message: "Telegram group is required",
        test: (value) => value !== "none",
      }),
    otherwise: (schema) => schema.notRequired(),
  }),
  url_redirect: yup
    .string()
    .trim()
    .nullable()
    .when("isUrlRedirect", {
      is: (isUrlRedirect: boolean) => isUrlRedirect,
      then: (schema) => schema.required("Redirect URL is required").urlWithEmoji().trim(),
      otherwise: (schema) => schema.notRequired(),
    }),
  enable_currency_conversion: yup.bool().default(true).optional(),
  selected_currencies: yup.array().of(yup.string()).default([]).optional(),
});

export type CreateStoreServiceBasicFormStepFormValuesYup = yup.InferType<
  typeof createStoreServiceBasicFormStepFormValidationSchema
>;
export type CreateStoreServiceBasicFormStepFormValues =
  StoreServiceGenInfoFormFieldsType &
    StoreServiceSettingsFormFieldsType &
    StoreServicePricingFormFieldsType &
    StoreServiceDeliveryTimeFrameFormFields;
